import React, { useCallback, useEffect, useRef, useState } from "react";
import "./region-picker.css";

/**
 * Drag a rectangle over a render of the TradingView energy layout.
 *
 * Deliberately plain divs rather than a canvas: a canvas would need its backing
 * store kept in step with the display size and the device pixel ratio, and every
 * one of those is a chance for the rectangle to land somewhere other than where
 * it was drawn. A bordered div cannot drift.
 *
 * Coordinates are the render's CSS pixels, which is what the server stores. The
 * scale comes from `viewport`, never from the image's natural size: the png is
 * viewport x deviceScaleFactor physical pixels, so dividing by the displayed
 * width against the wrong reference silently scales every region by the DPI.
 */
const COLORS = { SPY: "#f321ec", QQQ: "#21f3a5" };

function normalize(a, b) {
  return {
    x: Math.round(Math.min(a.x, b.x)),
    y: Math.round(Math.min(a.y, b.y)),
    width: Math.round(Math.abs(a.x - b.x)),
    height: Math.round(Math.abs(a.y - b.y)),
  };
}

export default function RegionPicker({ src, viewport, panes, regions, symbol, onPick }) {
  const containerRef = useRef(null);
  const dragRef = useRef(null);
  const [scale, setScale] = useState(0);
  const [draft, setDraft] = useState(null);

  // Re-measured on load, on resize, and whenever the image changes -- the bug to
  // avoid is measuring once and then being wrong for the rest of the session.
  const measure = useCallback(() => {
    const el = containerRef.current;
    if (!el || !viewport) {
      return;
    }
    setScale(el.getBoundingClientRect().width / viewport.width);
  }, [viewport]);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure, src]);

  const toRenderPx = (event) => {
    const rect = containerRef.current.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) / scale,
      y: (event.clientY - rect.top) / scale,
    };
  };

  const onMouseDown = (event) => {
    if (!scale) {
      return;
    }
    event.preventDefault();
    const start = toRenderPx(event);
    dragRef.current = start;
    setDraft({ x: start.x, y: start.y, width: 0, height: 0 });
  };

  const onMouseMove = (event) => {
    if (!dragRef.current) {
      return;
    }
    setDraft(normalize(dragRef.current, toRenderPx(event)));
  };

  const onMouseUp = (event) => {
    if (!dragRef.current) {
      return;
    }
    const rect = normalize(dragRef.current, toRenderPx(event));
    dragRef.current = null;
    setDraft(null);
    if (rect.width < 8 || rect.height < 8) {
      return;
    }

    // Anchor to whichever pane contains the rectangle, so a TradingView sidebar
    // resize moves the crop with the chart instead of leaving it behind. A
    // rectangle outside every pane stays viewport-absolute.
    const pane = (panes || []).find(
      (candidate) =>
        rect.x >= candidate.x &&
        rect.y >= candidate.y &&
        rect.x + rect.width <= candidate.x + candidate.width &&
        rect.y + rect.height <= candidate.y + candidate.height
    );
    onPick(
      pane
        ? { ...rect, x: Math.round(rect.x - pane.x), y: Math.round(rect.y - pane.y), paneIndex: pane.index }
        : { ...rect, paneIndex: null }
    );
  };

  /** Stored region -> a box in displayed pixels. */
  const boxFor = (region) => {
    if (!region || !scale) {
      return null;
    }
    const pane =
      region.paneIndex === null || region.paneIndex === undefined
        ? { x: 0, y: 0 }
        : (panes || []).find((candidate) => candidate.index === region.paneIndex);
    if (!pane) {
      return null;
    }
    return {
      left: (pane.x + region.x) * scale,
      top: (pane.y + region.y) * scale,
      width: region.width * scale,
      height: region.height * scale,
    };
  };

  return (
    <div
      className="region-picker"
      ref={containerRef}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
    >
      <img src={src} alt="TradingView energy layout" onLoad={measure} draggable={false} />

      {Object.keys(regions || {}).map((key) => {
        const box = boxFor(regions[key]);
        if (!box) {
          return null;
        }
        return (
          <div
            key={key}
            className={`region-box${key === symbol ? " active" : ""}`}
            style={{ ...box, borderColor: COLORS[key] || "#ffffff" }}
          >
            <span style={{ background: COLORS[key] || "#ffffff" }}>{key}</span>
          </div>
        );
      })}

      {draft && (
        <div
          className="region-box draft"
          style={{
            left: draft.x * scale,
            top: draft.y * scale,
            width: draft.width * scale,
            height: draft.height * scale,
            borderColor: COLORS[symbol] || "#ffffff",
          }}
        />
      )}
    </div>
  );
}
